// How a value looks in a cell, a card or a record, by field type.
import type { ReactNode } from "react";
import type { Approval, Field, Links, Person, Value } from "./pApi";
import { initials, shownType, text, toneOf } from "./format";

export function Pill({ f, name }: { f: Field; name: string }) {
  const t = toneOf(f, name);
  return <span className="pv-pill" style={{ background: t.bg, color: t.fg }}>{name}</span>;
}
export function Avatar({ name, size = 20 }: { name: string | null; size?: number }) {
  return <span className="pv-av" style={{ width: size, height: size, fontSize: size * 0.45 }} aria-hidden="true">{initials(name)}</span>;
}
export function ApprovalBadge({ a }: { a: Approval }) {
  const cls = a.status === "approved" ? "ok" : a.status === "rejected" ? "danger" : "warn";
  return <span className={`status ${cls}`} title={a.note ?? undefined}>{a.status === "pending" ? `Waiting · ${a.approverName ?? ""}` : `${a.status === "approved" ? "Approved" : "Rejected"} · ${a.approverName ?? ""}`}</span>;
}

/** The value of one field, formatted for display. `compact` keeps it to one line. */
export function CellValue({ f, v, links, allFields, fieldsOf }: { f: Field; v: Value | undefined; links?: Links; allFields?: Field[]; fieldsOf?: (id: string) => Field[] | undefined }): ReactNode {
  if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return f.type === "checkbox" ? <span className="pv-check" aria-label="No" /> : null;
  switch (f.type) {
    case "select": return <Pill f={f} name={String(v)} />;
    case "multiselect": return <span className="pv-pills">{(v as string[]).map((x) => <Pill key={x} f={f} name={x} />)}</span>;
    case "checkbox": return <span className={`pv-check ${v ? "on" : ""}`} aria-label={v ? "Yes" : "No"}>{v ? "✓" : ""}</span>;
    case "person": { const p = v as Person; return <span className="pv-person"><Avatar name={p.name} />{p.name}</span>; }
    case "approval": return <ApprovalBadge a={v as Approval} />;
    case "url": return <a href={String(v)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{String(v).replace(/^https?:\/\//, "")}</a>;
    case "email": return <a href={`mailto:${v}`} onClick={(e) => e.stopPropagation()}>{String(v)}</a>;
    case "link": {
      const ids = v as string[]; const t = f.options.table ? links?.[f.options.table] : undefined;
      return <span className="pv-chips">{ids.map((id) => <span key={id} className="pv-chip">{t?.rows.find((r) => r.id === id)?.label ?? (t?.restricted ? "Restricted" : "…")}</span>)}</span>;
    }
    case "lookup": {
      const st = allFields ? shownType(f, fieldsOf, allFields) : "text";
      const link = allFields?.find((x) => x.key === f.options.link);
      const src = link?.options.table ? fieldsOf?.(link.options.table)?.find((x) => x.key === f.options.field) : undefined;
      if ((st === "select" || st === "multiselect") && src) return <span className="pv-pills">{(v as string[]).map((x, i) => <Pill key={i} f={src} name={String(x)} />)}</span>;
      return <span className="pv-muted-list">{text(f, v, links)}</span>;
    }
    case "currency": case "number": case "percent": case "autonumber": case "rollup":
      return <span className="pv-num">{text(f, v, links)}</span>;
    case "formula":
      if (f.resultType === "checkbox") return <span className={`pv-check ${v ? "on" : ""}`}>{v ? "✓" : ""}</span>;
      return <span className={f.resultType === "number" ? "pv-num" : undefined}>{text(f, v, links)}</span>;
    default: return text(f, v, links);
  }
}

export const numeric = (f: Field) => ["number", "currency", "percent", "autonumber"].includes(f.type) || (f.type === "rollup" && !["concat", "earliest", "latest"].includes(f.options.fn ?? "")) || (f.type === "formula" && f.resultType === "number");
